import axios from 'axios';
import { createApiClient, publicFetch } from './httpClient';

const API_BASE_URL = process.env.REACT_APP_REST_BASE_URL;

// Create axios instance for settings API
const settingsApi = createApiClient({
  baseURL: `${API_BASE_URL}/settings`,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

//
// ============================
// Email Settings API calls
// ============================
//

/**
 * Fetch email settings from the backend
 * @returns {Promise<Object>} Email configuration object
 */
export const getEmailSettings = async () => {
  try {
    const response = await settingsApi.get('/email');
    return response.data;
  } catch (error) {
    console.error('Error fetching email settings:', error);
    throw error;
  }
};

/**
 * Update email settings on the backend
 * @param {Object} emailSettings - Email configuration object
 * @returns {Promise<Object>} Updated email configuration
 */
export const updateEmailSettings = async (emailSettings) => {
  try {
    const response = await settingsApi.put('/email', emailSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating email settings:', error);
    throw error;
  }
};

/**
 * Test email configuration by sending a test email
 * @param {string} recipientEmail - Email address to send test to
 * @returns {Promise<Object>} Test result
 */
export const testEmailSettings = async (recipientEmail) => {
  try {
    const response = await settingsApi.post('/email/test', {
      recipient_email: recipientEmail
    });
    return response.data;
  } catch (error) {
    console.error('Error testing email settings:', error);
    throw error;
  }
};

//
// ============================
// AI Settings API calls
// ============================
//

/**
 * Fetch AI settings from the backend
 * @returns {Promise<Object>} AI configuration object
 */
export const getAISettings = async () => {
  try {
    const response = await settingsApi.get('/ai');
    return response.data;
  } catch (error) {
    console.error('Error fetching AI settings:', error);
    throw error;
  }
};

/**
 * Update AI settings on the backend
 * @param {Object} aiSettings - AI configuration object
 * @returns {Promise<Object>} Updated AI configuration
 */
export const updateAISettings = async (aiSettings) => {
  try {
    const response = await settingsApi.put('/ai', aiSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating AI settings:', error);
    throw error;
  }
};

//
// ============================
// Interview Settings API calls
// ============================
//

/**
 * Fetch interview settings from the backend
 * @returns {Promise<Object>} Interview configuration object with MARKS_PER_QUESTION, PASSING_SCORE_PERCENTAGE, INTERVIEW_LINK_EXPIRY
 */
export const getInterviewSettings = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/settings/interview`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching interview settings:', error);
    throw error;
  }
};

/**
 * Update interview settings on the backend
 * @param {Object} interviewSettings - Interview configuration object
 * @returns {Promise<Object>} Updated interview configuration
 */
export const updateInterviewSettings = async (interviewSettings) => {
  try {
    const response = await settingsApi.put('/interview', interviewSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating interview settings:', error);
    throw error;
  }
};

/**
 * Fetch JobDiva settings from the backend
 * @returns {Promise<Object>} JobDiva configuration object with masked password
 */
export const getJobDivaSettings = async () => {
  try {
    const response = await settingsApi.get('/jobdiva');
    return response.data;
  } catch (error) {
    console.error('Error fetching JobDiva settings:', error);
    throw error;
  }
};

/**
 * Update JobDiva settings on the backend
 * @param {Object} jobDivaSettings - JobDiva configuration object
 * @returns {Promise<Object>} Updated JobDiva configuration
 */
export const updateJobDivaSettings = async (jobDivaSettings) => {
  try {
    const response = await settingsApi.put('/jobdiva', jobDivaSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating JobDiva settings:', error);
    throw error;
  }
};

//
// ============================
// Proctoring Settings API calls
// ============================
//

/**
 * Fetch proctoring settings from the backend
 * @returns {Promise<Object>} Proctoring configuration object
 */
export const getProctoringSettings = async () => {
  try {
    const response = await settingsApi.get('/proctoring');
    return response.data;
  } catch (error) {
    console.error('Error fetching proctoring settings:', error);
    throw error;
  }
};

/**
 * Update proctoring settings on the backend
 * @param {Object} proctoringSettings - Proctoring configuration object
 * @returns {Promise<Object>} Updated proctoring configuration
 */
export const updateProctoringSettings = async (proctoringSettings) => {
  try {
    const response = await settingsApi.put('/proctoring', proctoringSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating proctoring settings:', error);
    throw error;
  }
};

//
// ============================
// Listing Settings API calls
// ============================
//

/**
 * Fetch listing settings from the backend
 * @returns {Promise<Object>} Listing configuration object with JOB_OPENING_DEFAULT_DAYS and SCHEDULE_DEFAULT_DAYS
 */
export const getListingSettings = async () => {
  try {
    const response = await settingsApi.get('/listing');
    return response.data;
  } catch (error) {
    console.error('Error fetching listing settings:', error);
    throw error;
  }
};

/**
 * Update listing settings on the backend
 * @param {Object} listingSettings - Listing configuration object with JOB_OPENING_DEFAULT_DAYS and SCHEDULE_DEFAULT_DAYS
 * @returns {Promise<Object>} Updated listing configuration
 */
export const updateListingSettings = async (listingSettings) => {
  try {
    const response = await settingsApi.put('/listing', listingSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating listing settings:', error);
    throw error;
  }
};

//
// ============================
// RBAC Settings API calls
// ============================
//

/**
 * Fetch RBAC settings from the backend
 * @returns {Promise<Object>} RBAC configuration object
 */
export const getRBACSettings = async () => {
  try {
    const response = await settingsApi.get('/rbac');
    return response.data;
  } catch (error) {
    console.error('Error fetching RBAC settings:', error);
    throw error;
  }
};

/**
 * Update RBAC settings on the backend
 * @param {Object} rbacSettings - RBAC configuration object
 * @returns {Promise<Object>} Updated RBAC configuration
 */
export const updateRBACSettings = async (rbacSettings) => {
  try {
    const response = await settingsApi.put('/rbac', rbacSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating RBAC settings:', error);
    throw error;
  }
};

//
// ============================
// Avatar Settings API calls
// ============================
//

/**
 * Get current avatar mode
 * @returns {Promise<Object>} Current avatar mode configuration
 */
export const getCurrentAvatarMode = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/avatar/current-avatar`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching current avatar mode:', error);
    throw error;
  }
};

/**
 * Update avatar mode
 * @param {Object} modeData - Object with mode property (e.g., { mode: 'did' })
 * @returns {Promise<Object>} Update response with message and avatar_mode
 */
export const updateAvatarMode = async (modeData) => {
  try {
    const avatarApi = createApiClient({
      baseURL: `${API_BASE_URL.replace('/settings', '')}/avatar`,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await avatarApi.post('/update-avatar', modeData);
    return response.data;
  } catch (error) {
    console.error('Error updating avatar mode:', error);
    throw error;
  }
};

/**
 * Add or update a provider API key
 * @param {Object} providerData - Object with provider and api_key properties
 * @returns {Promise<Object>} Response with message, provider, and status
 */
export const saveProviderKey = async (providerData) => {
  try {
    const avatarApi = createApiClient({
      baseURL: `${API_BASE_URL.replace('/settings', '')}/avatar`,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await avatarApi.post('/provider-key', providerData);
    return response.data;
  } catch (error) {
    console.error('Error saving provider key:', error);
    throw error;
  }
};

/**
 * Get provider API key
 * @param {string} provider - Provider name (e.g., 'did', 'heygen')
 * @returns {Promise<Object>} Provider data with api_key
 */
export const getProviderKey = async (provider) => {
  try {
    const avatarApi = createApiClient({
      baseURL: `${API_BASE_URL.replace('/settings', '')}/avatar`,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await avatarApi.get(`/provider-key/${provider}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching provider key for ${provider}:`, error);
    throw error;
  }
};

/**
 * Get all available provider keys
 * @returns {Promise<Object>} Object with providers array and count
 */
export const getAllProviderKeys = async () => {
  try {
    const avatarApi = createApiClient({
      baseURL: `${API_BASE_URL.replace('/settings', '')}/avatar`,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await avatarApi.get('/all-provider-keys');
    return response.data;
  } catch (error) {
    console.error('Error fetching all provider keys:', error);
    throw error;
  }
};

/**
 * Fetch avatar settings from the backend
 * @returns {Promise<Object>} Avatar configuration object
 */
export const getAvatarSettings = async () => {
  try {
    const response = await settingsApi.get('/avatar');
    return response.data;
  } catch (error) {
    console.error('Error fetching avatar settings:', error);
    throw error;
  }
};

/**
 * Update avatar settings on the backend
 * @param {Object} avatarSettings - Avatar configuration object
 * @returns {Promise<Object>} Updated avatar configuration
 */
export const updateAvatarSettings = async (avatarSettings) => {
  try {
    const response = await settingsApi.put('/avatar', avatarSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating avatar settings:', error);
    throw error;
  }
};

//
// ============================
// Branding Settings API calls
// ============================
//

/**
 * Fetch branding settings from the backend
 * @returns {Promise<Object>} Branding configuration object
 */
export const getBrandingSettings = async () => {
  try {
    const response = await settingsApi.get('/branding');
    return response.data;
  } catch (error) {
    console.error('Error fetching branding settings:', error);
    throw error;
  }
};

/**
 * Update branding settings on the backend
 * @param {Object} brandingSettings - Branding configuration object
 * @returns {Promise<Object>} Updated branding configuration
 */
export const updateBrandingSettings = async (brandingSettings) => {
  try {
    const response = await settingsApi.put('/branding', brandingSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating branding settings:', error);
    throw error;
  }
};

//
// ============================
// System Service Settings API calls
// ============================
//

/**
 * Fetch system service settings from the backend
 * @returns {Promise<Object>} System service configuration object with SYSTEM_CHECK_ENABLE and SECOND_CAMERA_ENABLE
 */
export const getSystemServiceSettings = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/settings/system-service`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching system service settings:', error);
    throw error;
  }
};

/**
 * Update system service settings on the backend
 * @param {Object} systemServiceSettings - System service configuration object
 * @returns {Promise<Object>} Updated system service configuration
 */
export const updateSystemServiceSettings = async (systemServiceSettings) => {
  try {
    const response = await settingsApi.put('/system-service', systemServiceSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating system service settings:', error);
    throw error;
  }
};

//
// ============================
// LLM Settings API calls
// ============================
//

/**
 * Fetch all LLM settings from the backend
 * @returns {Promise<Object>} All LLM configurations
 */
export const getLLMSettings = async () => {
  try {
    const response = await settingsApi.get('/llm');
    return response.data;
  } catch (error) {
    console.error('Error fetching LLM settings:', error);
    throw error;
  }
};

/**
 * Fetch all available LLM configurations from the database
 * @param {boolean} activeOnly - If true, only fetch active configurations
 * @returns {Promise<Array>} Array of LLM configurations with provider_name, model_name, etc.
 */
export const getLLMConfigs = async (activeOnly = true) => {
  try {
    const llmConfigApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await llmConfigApi.get('/llm_config/get-llm', {
      params: { active_only: activeOnly }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching LLM configs:', error);
    throw error;
  }
};

/**
 * Create or update LLM configuration
 * @param {Object} llmConfig - LLM configuration object with provider_name, model_name, temperature, max_tokens, response_time_ms, is_active
 * @returns {Promise<Object>} Created/updated LLM configuration response
 */
export const createLLMConfig = async (llmConfig) => {
  try {
    const llmConfigApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await llmConfigApi.post('/llm_config/create-llm', llmConfig);
    return response.data;
  } catch (error) {
    console.error('Error creating/updating LLM config:', error);
    throw error;
  }
};

/**
 * Update interview LLM settings
 * @param {Object} interviewConfig - Interview LLM configuration
 * @returns {Promise<Object>} Updated interview LLM configuration
 */
export const updateInterviewLLMSettings = async (interviewConfig) => {
  try {
    const response = await settingsApi.put('/llm/interview', interviewConfig);
    return response.data;
  } catch (error) {
    console.error('Error updating interview LLM settings:', error);
    throw error;
  }
};

/**
 * Update resume screening LLM settings
 * @param {Object} resumeConfig - Resume screening LLM configuration
 * @returns {Promise<Object>} Updated resume screening LLM configuration
 */
export const updateResumeLLMSettings = async (resumeConfig) => {
  try {
    const response = await settingsApi.put('/llm/analyse-resume', resumeConfig);
    return response.data;
  } catch (error) {
    console.error('Error updating resume LLM settings:', error);
    throw error;
  }
};

/**
 * Update skill extractor LLM settings
 * @param {Object} skillConfig - Skill extractor LLM configuration
 * @returns {Promise<Object>} Updated skill extractor LLM configuration
 */
export const updateSkillExtractorLLMSettings = async (skillConfig) => {
  try {
    const response = await settingsApi.put('/llm/skill-extractor', skillConfig);
    return response.data;
  } catch (error) {
    console.error('Error updating skill extractor LLM settings:', error);
    throw error;
  }
};

/**
 * Update ATS score LLM settings
 * @param {Object} atsConfig - ATS score LLM configuration
 * @returns {Promise<Object>} Updated ATS score LLM configuration
 */
export const updateATSScoreLLMSettings = async (atsConfig) => {
  try {
    const response = await settingsApi.put('/llm/ats-score', atsConfig);
    return response.data;
  } catch (error) {
    console.error('Error updating ATS score LLM settings:', error);
    throw error;
  }
};

//
// ============================
// Prompts API calls
// ============================
//

/**
 * Fetch all AI prompts from the backend
 * @returns {Promise<Object>} Object containing all prompts with their keys as property names
 */
export const getAllPrompts = async () => {
  try {
    const promptsApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await promptsApi.get('/prompts/');
    return response.data;
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

/**
 * Update a specific AI prompt
 * @param {string} promptName - The name/key of the prompt to update
 * @param {string} promptText - The new prompt text
 * @returns {Promise<Object>} Updated prompt response
 */
export const updatePrompt = async (promptName, promptText) => {
  try {
    const promptsApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await promptsApi.post(`/prompts/${promptName}`, {
      prompt: promptText
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating prompt ${promptName}:`, error);
    throw error;
  }
};

//
// ============================
// Roles API calls
// ============================
//

/**
 * Fetch list of available roles
 * @returns {Promise<Array>} Array of role objects with id, name, description, isActive, etc.
 */
export const getRoles = async () => {
  try {
    const rolesApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '/user_roles'),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await rolesApi.get('/user_roles/roles');
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

//
// ============================
// TTS Voices API calls
// ============================
//

/**
 * Fetch available TTS voices from the backend
 * @returns {Promise<Object>} Object containing provider and voices array with voice details (name, gender, accent, type)
 */
export const getTTSVoices = async () => {
  try {
    const ttsVoicesApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await ttsVoicesApi.get('/tts_voices/voices');
    return response.data;
  } catch (error) {
    console.error('Error fetching TTS voices:', error);
    throw error;
  }
};

/**
 * Update the default TTS voice
 * @param {string} voiceName - The name of the voice to set as default
 * @returns {Promise<Object>} Update response with success message
 */
export const updateDefaultTTSVoice = async (voiceName) => {
  try {
    const ttsVoicesApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await ttsVoicesApi.put('/tts_voices/voices/default', {
      voice: voiceName
    });
    return response.data;
  } catch (error) {
    console.error('Error updating default TTS voice:', error);
    throw error;
  }
};

//
// ============================
// Voice Provider API calls
// ============================
//

/**
 * Get all voice providers (STT and TTS)
 * @returns {Promise<Array>} Array of all available providers with their configurations
 */
export const getAllVoiceProviders = async () => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.get('/voice-providers');
    return response.data;
  } catch (error) {
    console.error('Error fetching voice providers:', error);
    throw error;
  }
};

/**
 * Get voice providers by type (STT or TTS)
 * @param {string} providerType - Type of provider: 'stt' or 'tts'
 * @returns {Promise<Array>} Array of providers matching the type
 */
export const getVoiceProvidersByType = async (providerType) => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/voice_provider/type/${providerType}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${providerType} providers:`, error);
    throw error;
  }
};

/**
 * Switch the active STT provider
 * @param {string} provider - Provider name (e.g., 'browser', 'azure', 'deepgram')
 * @returns {Promise<Object>} Success message with the switched provider
 */
export const switchSTTProvider = async (provider) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.put(`/switch-stt?provider=${provider}`);
    return response.data;
  } catch (error) {
    console.error('Error switching STT provider:', error);
    throw error;
  }
};

/**
 * Switch the active TTS provider
 * @param {string} provider - Provider name (e.g., 'browser', 'azure', 'elevenlabs', 'openai')
 * @returns {Promise<Object>} Success message with the switched provider
 */
export const switchTTSProvider = async (provider) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.put(`/switch-tts?provider=${provider}`);
    return response.data;
  } catch (error) {
    console.error('Error switching TTS provider:', error);
    throw error;
  }
};

/**
 * Get a specific provider configuration
 * @param {string} providerName - Provider name (e.g., 'azure', 'elevenlabs')
 * @returns {Promise<Object>} Provider configuration with all details
 */
export const getVoiceProvider = async (providerName) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.get(`/${providerName}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching provider ${providerName}:`, error);
    throw error;
  }
};

/**
 * Update a voice provider configuration
 * @param {string} providerName - Provider name to update
 * @param {Object} providerConfig - Configuration object with type, provider, model, api_key, region, voice_id, voice_name
 * @returns {Promise<Object>} Updated provider configuration
 */
export const updateVoiceProvider = async (providerName, providerConfig) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.put(`/${providerName}`, providerConfig);
    return response.data;
  } catch (error) {
    console.error(`Error updating provider ${providerName}:`, error);
    throw error;
  }
};

/**
 * Delete a voice provider
 * @param {string} providerName - Provider name to delete
 * @returns {Promise<Object>} Success message
 */
export const deleteVoiceProvider = async (providerName) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.delete(`/${providerName}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting provider ${providerName}:`, error);
    throw error;
  }
};

/**
 * Get active voice providers
 * @returns {Promise<Object>} Object containing active STT and TTS providers
 */
export const getActiveVoiceProviders = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/voice_provider/active-providers`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching active voice providers:', error);
    throw error;
  }
};

/**
 * Create a new voice provider
 * @param {Object} providerConfig - Configuration object for the new provider
 * @returns {Promise<Object>} Created provider configuration
 */
export const createVoiceProvider = async (providerConfig) => {
  try {
    const voiceProviderApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', '') + '/voice_provider',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await voiceProviderApi.post('/create-providers', providerConfig);
    return response.data;
  } catch (error) {
    console.error('Error creating voice provider:', error);
    throw error;
  }
};

//
// ============================
// Language Configuration API calls
// ============================
//

/**
 * Get the current global language setting
 * @returns {Promise<Object>} Current language configuration with language property
 */
export const getCurrentLanguage = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL.replace('/settings', '')}/language_config/language/current`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching current language:', error);
    throw error;
  }
};

/**
 * Get all supported languages
 * @returns {Promise<Object>} Object containing supported_languages array and default_language
 */
export const getSupportedLanguages = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL.replace('/settings', '')}/language_config/language/supported`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching supported languages:', error);
    throw error;
  }
};

export const getAvailableInterviewLanguages = async (configuration = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(configuration).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const query = params.toString();
    const response = await publicFetch(`${API_BASE_URL.replace('/settings', '')}/language_config/interview/available${query ? `?${query}` : ''}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching available interview languages:', error);
    throw error;
  }
};

/**
 * Set the global language
 * @param {string} language - The language to set globally
 * @returns {Promise<Object>} Update response with message and language
 */
export const setGlobalLanguage = async (language) => {
  try {
    const languageApi = createApiClient({
      baseURL: API_BASE_URL.replace('/settings', ''),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const response = await languageApi.post('/language_config/language/set', {
      language: language
    });
    return response.data;
  } catch (error) {
    console.error('Error setting global language:', error);
    throw error;
  }
};

// Email Config APIs (FIXED)

const emailConfigApi = createApiClient({
  baseURL: `${API_BASE_URL.replace('/settings', '')}/config`,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

export const getEmailConfig = async () => {
  try {
    const response = await emailConfigApi.get('/email');
    return response.data;
  } catch (error) {
    console.error('Error fetching email config:', error);
    throw error;
  }
};

export const updateEmailConfig = async (emailConfig) => {
  const response = await emailConfigApi.put('/email', emailConfig);
  return response.data;
};

//
// ============================
// Video Proctoring Config API
// ============================
//

export const getVideoProctoringConfig = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/config/video-proctoring`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching video proctoring config:", error);
    throw error;
  }
};

export const updateVideoProctoringConfig = async (payload) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/config/video-proctoring`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating video proctoring config:", error);
    throw error;
  }
};

// Reminder Configuration APIs
export const getReminderConfig = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/reminders/get/config`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching reminder config:", error);
    throw error;
  }
};

export const updateReminderConfig = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/reminders/set/config`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating reminder config:", error);
    throw error;
  }
};
//
// ============================
// Interview Provider API calls
// ============================
//

/**
 * Fetch the current interview provider
 * @returns {Promise<Object>} Object with provider (legacy or gemini_live)
 */
export const getInterviewProvider = async () => {
  try {
    const response = await settingsApi.get('/interview-provider');
    return response.data;
  } catch (error) {
    console.error('Error fetching interview provider:', error);
    throw error;
  }
};

/**
 * Update the interview provider
 * @param {Object} providerData - Object with provider (legacy or gemini_live)
 * @returns {Promise<Object>} Update response with message
 */
export const updateInterviewProvider = async (providerData) => {
  try {
    const response = await settingsApi.put('/interview-provider', providerData);
    return response.data;
  } catch (error) {
    console.error('Error updating interview provider:', error);
    throw error;
  }
};

/** Get Gemini Live/static-avatar compatibility without exposing any secrets. */
export const getAvatarCompatibility = async () => {
  try {
    const response = await publicFetch(`${API_BASE_URL}/avatar/compatibility`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching avatar compatibility:', error);
    throw error;
  }
};

/** Get backend-owned Gemini Live voices and application preference options. */
export const getGeminiLiveOptions = async () => {
  try {
    const response = await settingsApi.get('/interview-provider/gemini-live/options');
    return response.data;
  } catch (error) {
    console.error('Error fetching Gemini Live options:', error);
    throw error;
  }
};

/**
 * Fetch the current interview mode
 * @returns {Promise<Object>} Object with mode (planned or dynamic)
 */
export const getInterviewMode = async () => {
  try {
    const response = await settingsApi.get('/interview-mode');
    return response.data;
  } catch (error) {
    console.error('Error fetching interview mode:', error);
    throw error;
  }
};

/**
 * Update the interview mode
 * @param {Object} modeData - Object with mode (planned or dynamic)
 * @returns {Promise<Object>} Update response with message
 */
export const updateInterviewMode = async (modeData) => {
  try {
    const response = await settingsApi.put('/interview-mode', modeData);
    return response.data;
  } catch (error) {
    console.error('Error updating interview mode:', error);
    throw error;
  }
};

export default {
  getEmailSettings,
  updateEmailSettings,
  testEmailSettings,
  getAISettings,
  updateAISettings,
  getInterviewSettings,
  updateInterviewSettings,
  getListingSettings,
  updateListingSettings,
  getRBACSettings,
  updateRBACSettings,
  getProctoringSettings,
  updateProctoringSettings,
  getCurrentAvatarMode,
  updateAvatarMode,
  getAvatarCompatibility,
  saveProviderKey,
  getProviderKey,
  getAllProviderKeys,
  getAvatarSettings,
  updateAvatarSettings,
  getBrandingSettings,
  updateBrandingSettings,
  getSystemServiceSettings,
  updateSystemServiceSettings,
  getLLMSettings,
  getLLMConfigs,
  createLLMConfig,
  updateInterviewLLMSettings,
  updateResumeLLMSettings,
  updateSkillExtractorLLMSettings,
  updateATSScoreLLMSettings,
  getAllPrompts,
  updatePrompt,
  getRoles,
  getTTSVoices,
  updateDefaultTTSVoice,
  getCurrentLanguage,
  getSupportedLanguages,
  setGlobalLanguage,
  getAllVoiceProviders,
  getVoiceProvidersByType,
  switchSTTProvider,
  switchTTSProvider,
  getVoiceProvider,
  updateVoiceProvider,
  deleteVoiceProvider,
  getActiveVoiceProviders,
  createVoiceProvider,
  getEmailConfig,
  updateEmailConfig,
  getVideoProctoringConfig,
  updateVideoProctoringConfig,
  getReminderConfig,
  updateReminderConfig,
  getInterviewProvider,
  updateInterviewProvider,
  getGeminiLiveOptions,
  getInterviewMode,
  updateInterviewMode
};
